import { useState, useRef, useCallback } from "react";
import { Mic, MicOff, CheckCircle, AlertTriangle, RotateCcw, Volume2, VolumeX } from "lucide-react";
import { Button } from "../../../components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;
const SILENCE_THRESHOLD_DB = -80;
const TEST_DURATION_MS = 5000;

async function measureVolume(blob) {
  const ctx = new AudioContext();
  const buf = await ctx.decodeAudioData(await blob.arrayBuffer());
  const data = buf.getChannelData(0);
  let sum = 0;
  for (let i = 0; i < data.length; i++) sum += data[i] * data[i];
  const rms = Math.sqrt(sum / data.length);
  ctx.close();
  return 20 * Math.log10(rms || 1e-10);
}

/**
 * MicrophoneTest — Full pre-assessment mic verification.
 * Records 5s, auto-plays, asks "Could you hear yourself?", saves recording to storage.
 * Props:
 *   token: candidate access token
 *   onResult: (status) => void — "passed" | "skipped"
 */
export default function MicrophoneTest({ token, onResult }) {
  const [state, setState] = useState("idle");
  // idle | recording | analyzing | playback | confirmed | failed | skipped
  const [volumeDb, setVolumeDb] = useState(null);
  const [testCount, setTestCount] = useState(0);
  const [error, setError] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const mediaRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const audioRef = useRef(null);
  const timerRef = useRef(null);
  const blobRef = useRef(null);

  const startTest = useCallback(async () => {
    setError(null);
    setVolumeDb(null);
    setElapsed(0);
    setState("recording");

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const recorder = new MediaRecorder(stream, { mimeType: "audio/webm;codecs=opus" });
      mediaRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        clearInterval(timerRef.current);
        setState("analyzing");

        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        blobRef.current = blob;
        const newCount = testCount + 1;
        setTestCount(newCount);

        try {
          const db = await measureVolume(blob);
          const rounded = Math.round(db * 10) / 10;
          setVolumeDb(rounded);

          // Upload mic test recording to storage
          const formData = new FormData();
          formData.append("audio_file", blob, `mic_test_${newCount}.webm`);
          formData.append("duration_seconds", String(TEST_DURATION_MS / 1000));
          formData.append("mean_volume_db", String(rounded));
          formData.append("test_number", String(newCount));
          fetch(`${API}/api/assessment/${token}/mic-test`, {
            method: "POST",
            body: formData,
          }).catch(() => {});

          if (rounded <= SILENCE_THRESHOLD_DB) {
            // Silence detected — go directly to failed state
            setState("failed");
            logResult(newCount, rounded, "silence_detected", false);
          } else {
            // Audio detected — play it back for candidate confirmation
            const url = URL.createObjectURL(blob);
            if (audioRef.current) {
              audioRef.current.src = url;
              audioRef.current.play().catch(() => {});
            }
            setState("playback");
          }
        } catch {
          setError("Could not analyze audio. Your browser may not support this feature.");
          setState("idle");
        }
      };

      recorder.start(500);

      // Elapsed timer
      let sec = 0;
      timerRef.current = setInterval(() => {
        sec++;
        setElapsed(sec);
      }, 1000);

      // Auto-stop after 5 seconds
      setTimeout(() => {
        if (recorder.state === "recording") recorder.stop();
      }, TEST_DURATION_MS);
    } catch {
      setError("Microphone access denied. Please allow microphone permissions and try again.");
      setState("idle");
    }
  }, [token, testCount]);

  const logResult = useCallback(
    (count, db, result, skipped) => {
      fetch(`${API}/api/assessment/${token}/mic-test-log`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          test_number: count,
          total_attempts: count,
          mean_volume_db: db,
          result: result,
          acknowledged_skip: skipped,
          browser: navigator.userAgent,
        }),
      }).catch(() => {});
    },
    [token]
  );

  const handleYes = () => {
    setState("confirmed");
    logResult(testCount, volumeDb, "passed", false);
    onResult?.("passed");
  };

  const handleNo = () => {
    setState("failed");
  };

  const handleSkip = () => {
    setState("skipped");
    logResult(testCount, volumeDb, "skipped", true);
    onResult?.("skipped");
  };

  const isDone = state === "confirmed" || state === "skipped";

  return (
    <div
      className="border border-gray-200 rounded-xl p-5 bg-white"
      data-testid="mic-test-section"
    >
      <h3 className="text-sm font-semibold text-gray-800 mb-1 flex items-center gap-2">
        <Mic className="w-4 h-4 text-[#00AEEF]" />
        Test Your Microphone
      </h3>
      <p className="text-xs text-gray-500 mb-3">
        This assessment includes voice recordings. Verify your microphone is working before you begin.
      </p>
      <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 mb-4">
        <p className="text-xs text-blue-800 font-medium mb-1">Voice Recording Rules <span className="text-red-500">*</span></p>
        <ul className="text-xs text-blue-700 space-y-0.5 list-disc list-inside">
          <li>Each voice recording must be at least <strong>5 seconds</strong> and at most <strong>3 minutes</strong></li>
          <li>You can record <strong>multiple voice notes</strong> per question</li>
          <li>You can also <strong>type your answer</strong> in addition to recording</li>
        </ul>
      </div>

      {/* Hidden audio element for playback */}
      <audio ref={audioRef} className="hidden" />

      {/* IDLE — initial state or after retry */}
      {state === "idle" && (
        <Button
          type="button"
          onClick={startTest}
          variant="outline"
          className="gap-2 text-[#00AEEF] border-[#00AEEF]/30 hover:bg-[#00AEEF]/5"
          data-testid="mic-test-btn"
        >
          <Mic className="w-4 h-4" />
          Test Your Microphone
        </Button>
      )}

      {/* RECORDING — 5 second countdown */}
      {state === "recording" && (
        <div className="flex items-center gap-3" data-testid="mic-test-recording">
          <div className="relative">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center animate-pulse">
              <Mic className="w-5 h-5 text-red-500" />
            </div>
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-ping" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-800">Recording... speak now</p>
            <p className="text-xs text-gray-500">
              Say something for {Math.max(0, 5 - elapsed)} more second{5 - elapsed !== 1 ? "s" : ""}
            </p>
            <div className="w-32 h-1.5 bg-gray-200 rounded-full mt-1.5 overflow-hidden">
              <div
                className="h-full bg-red-500 rounded-full transition-all duration-1000"
                style={{ width: `${(elapsed / 5) * 100}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* ANALYZING */}
      {state === "analyzing" && (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <RotateCcw className="w-5 h-5 text-blue-500 animate-spin" />
          </div>
          <p className="text-sm text-gray-600">Analyzing audio...</p>
        </div>
      )}

      {/* PLAYBACK — audio detected, playing back, asking confirmation */}
      {state === "playback" && (
        <div className="space-y-3" data-testid="mic-test-playback">
          <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <Volume2 className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium text-blue-800">Playing back your recording...</p>
              <p className="text-xs text-blue-600 mt-0.5">Volume: {volumeDb} dB</p>
            </div>
          </div>
          <p className="text-sm font-medium text-gray-800">Could you hear yourself clearly?</p>
          <div className="flex gap-2">
            <Button
              type="button"
              onClick={handleYes}
              className="gap-1.5 bg-green-600 hover:bg-green-700 text-white"
              data-testid="mic-test-yes"
            >
              <CheckCircle className="w-4 h-4" />
              Yes, sounds good
            </Button>
            <Button
              type="button"
              onClick={handleNo}
              variant="outline"
              className="gap-1.5 text-gray-600"
              data-testid="mic-test-no"
            >
              No, try again
            </Button>
          </div>
        </div>
      )}

      {/* FAILED — silence detected or candidate said "No" */}
      {state === "failed" && (
        <div className="space-y-3" data-testid="mic-test-failed">
          <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium text-amber-800">
                {volumeDb !== null && volumeDb <= SILENCE_THRESHOLD_DB
                  ? "We did not detect any audio. Please check that your microphone is connected and not muted."
                  : "Let's try again. Make sure you can hear yourself clearly."}
              </p>
            </div>
          </div>

          {/* Troubleshooting tips */}
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
            <p className="text-xs font-semibold text-gray-700 mb-2">Troubleshooting tips:</p>
            <ul className="text-xs text-gray-600 space-y-1.5">
              <li>Make sure your microphone is not muted</li>
              <li>Try using headphones with a built-in microphone</li>
              <li>Check your browser permissions — click the lock icon in the address bar and make sure microphone access is set to <strong>Allow</strong></li>
              <li>Close other apps that may be using the microphone (Zoom, Teams, etc.)</li>
            </ul>
          </div>

          <div className="flex gap-2">
            <Button
              type="button"
              onClick={() => {
                setState("idle");
                startTest();
              }}
              className="gap-1.5 bg-[#00AEEF] hover:bg-[#0090c5] text-white"
              data-testid="mic-test-retry"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Try Again
            </Button>
          </div>

          {/* Skip option */}
          <div className="pt-2 border-t border-gray-200">
            <button
              type="button"
              onClick={handleSkip}
              className="text-xs text-gray-500 hover:text-gray-700 underline underline-offset-2"
              data-testid="mic-test-skip"
            >
              I want to proceed without voice recording — I will type all my responses
            </button>
          </div>
        </div>
      )}

      {/* CONFIRMED — mic test passed */}
      {state === "confirmed" && (
        <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-200" data-testid="mic-test-passed">
          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-green-800">
              Microphone verified — you are ready to begin.
            </p>
            <p className="text-xs text-green-600 mt-0.5">
              Volume: {volumeDb} dB | Attempts: {testCount}
            </p>
          </div>
        </div>
      )}

      {/* SKIPPED — candidate acknowledged skip */}
      {state === "skipped" && (
        <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200" data-testid="mic-test-skipped">
          <VolumeX className="w-5 h-5 text-gray-400 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-gray-700">
              Proceeding without voice recording — all responses will be typed.
            </p>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 mt-3 p-3 bg-red-50 rounded-lg border border-red-200" data-testid="mic-test-error">
          <MicOff className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
          <p className="text-xs text-red-700">{error}</p>
        </div>
      )}
    </div>
  );
}
